import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment,
  Paper
} from '@mui/material';
import { Email, ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getCleanErrorMessage } from '../../utils/errorUtils';

interface PasswordResetFormProps {
  onSuccess: () => void;
  onSwitchToLogin: () => void;
}

const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const { t } = useTranslation();
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic email validation
    if (!email.trim()) {
      setError(t('auth.emailRequired'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('auth.invalidEmail'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await resetPassword(email);
      setSuccess(true);
      console.log('✅ Password reset email sent successfully');
    } catch (err: any) {
      console.error('❌ Password reset failed:', err);
      setError(getCleanErrorMessage(err) || t('auth.resetPasswordError'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom color="success.main">
            {t('auth.resetPasswordSuccess')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('auth.resetPasswordSuccessMessage')}
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Check your email:</strong> We've sent a password reset link to <strong>{email}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Can't find the email?</strong> Check your spam folder or try again in a few minutes.
            </Typography>
          </Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={onSwitchToLogin}
            startIcon={<ArrowBack />}
          >
            {t('auth.backToLogin')}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('auth.resetPassword')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auth.resetPasswordSubtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label={t('auth.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? t('common.loading') : t('auth.sendResetEmail')}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Link
            component="button"
            type="button"
            onClick={onSwitchToLogin}
            sx={{ fontSize: '0.875rem' }}
          >
            {t('auth.backToLogin')}
          </Link>
        </Box>
      </Box>
    </Paper>
  );
};

export default PasswordResetForm;
